import {
  ACTION,
  ButtonVariants,
  Dialog,
  IconName,
  QuoteStatusEnum,
  Text,
  useCallbackRef,
  useDisclosure,
  useGlobalToasts,
  useSocketClient,
  type Quote,
} from '@talos/kyoko';
import type { MenuItemDef } from 'ag-grid-community';
import { useMemo, useState, type ReactNode } from 'react';
import {
  cancelMultipleQuotes,
  type CancelMultipleQuotesDeps,
} from '../../../../components/OMS/NewRFQ/cancelMultipleQuotes';
import { useRoleAuth, useUser } from '../../../../hooks';
import { SweepGroupIDRow, type QuotesBlotterRow } from '../../QuotesV2/types';

interface UseCancelOpenQuotesMenuItemParams {
  quoteSub: CancelMultipleQuotesDeps['quoteSub'];
}

interface UseCancelOpenQuotesMenuItemResult {
  getCancelOpenQuotesMenuItem: (row: QuotesBlotterRow) => MenuItemDef | null;
  dialog: ReactNode;
}

/**
 * Builds the sweep-group "Cancel open quotes" context menu behavior.
 *
 * The hook keeps the menu integration small by owning the transient quote selection,
 * confirmation dialog, websocket dependencies, and the actual bulk-cancel call.
 * The quote stream is passed in by the blotter so this does not create a second
 * subscription just to observe cancel feedback.
 * Only sweep-group rows can produce a menu item; groups without open quote-level
 * statuses still show the item, but disabled, so users understand why no action is
 * available.
 */
export function useCancelOpenQuotesMenuItem({
  quoteSub,
}: UseCancelOpenQuotesMenuItemParams): UseCancelOpenQuotesMenuItemResult {
  const webSocketClient = useSocketClient();
  const { add: addToast } = useGlobalToasts();
  const user = useUser();
  const { isAuthorized } = useRoleAuth();
  const cancelOpenQuotesDialog = useDisclosure();
  const [quotesToCancel, setQuotesToCancel] = useState<Quote[]>([]);
  const [isCanceling, setIsCanceling] = useState(false);

  const handleCancelOpenQuotes = useCallbackRef(async () => {
    setIsCanceling(true);
    try {
      await cancelMultipleQuotes(quotesToCancel, {
        webSocketClient,
        quoteSub,
        addToast,
      });
    } finally {
      setIsCanceling(false);
    }
  });

  const getCancelOpenQuotesMenuItem = useCallbackRef((row: QuotesBlotterRow): MenuItemDef | null => {
    if (!(row instanceof SweepGroupIDRow)) {
      return null;
    }

    const { openQuotes, disabled, tooltip } = getCancelOpenQuotesMenuItemState({
      row,
      username: user.Name,
      isAuthorizedToTradeRFQ: isAuthorized(ACTION.ACCEPT_RFQ),
    });

    return {
      name: `Cancel open quotes (${openQuotes.length})`,
      icon: `<i class="ag-icon ${IconName.Trash}"/>`,
      disabled,
      tooltip,
      action: () => {
        if (disabled) {
          return;
        }

        setQuotesToCancel(openQuotes);
        cancelOpenQuotesDialog.open();
      },
    };
  });

  const dialog = useMemo(
    () => (
      <Dialog
        {...cancelOpenQuotesDialog}
        title="Cancel Open Quotes"
        confirmLabel="Cancel Quotes"
        confirmDisabled={isCanceling}
        confirmLoading={isCanceling}
        variant={ButtonVariants.Negative}
        onConfirm={handleCancelOpenQuotes}
        dataTestId="quotes-v2-cancel-open-quotes-dialog"
      >
        <Text>
          Confirm cancellation of {quotesToCancel.length} open quote{quotesToCancel.length === 1 ? '' : 's'}.
        </Text>
      </Dialog>
    ),
    [cancelOpenQuotesDialog, handleCancelOpenQuotes, isCanceling, quotesToCancel.length]
  );

  return {
    getCancelOpenQuotesMenuItem,
    dialog,
  };
}

function getCancelOpenQuotesMenuItemState({
  row,
  username,
  isAuthorizedToTradeRFQ,
}: {
  row: SweepGroupIDRow;
  username: string;
  isAuthorizedToTradeRFQ: boolean;
}): { openQuotes: Quote[]; disabled: boolean; tooltip: string | undefined } {
  const openQuotes =
    row.SweepGroupID === SweepGroupIDRow.unassignedValue ? [] : getCancellableQuotes(row.quotes, username);

  if (!isAuthorizedToTradeRFQ) {
    return {
      openQuotes,
      disabled: true,
      tooltip: 'Not authorized to cancel open quotes',
    };
  }

  if (openQuotes.length === 0) {
    return {
      openQuotes,
      disabled: true,
      tooltip: 'No open quotes to cancel',
    };
  }

  return {
    openQuotes,
    disabled: false,
    tooltip: undefined,
  };
}

function getCancellableQuotes(quotes: Quote[], username: string): Quote[] {
  return quotes.filter(
    quote =>
      quote.User === username &&
      (quote.QuoteStatus === QuoteStatusEnum.Open || quote.QuoteStatus === QuoteStatusEnum.PendingNew)
  );
}
